import React, { useRef } from 'react';
import { useInView } from 'framer-motion';
import { motion } from '../../lib/motionShim';
import LiveScreen from '../LiveScreen';
import GhostNumeral from '../GhostNumeral';

const modes = [
  {
    key: 'For You',
    label: 'Everyday',
    copy: 'Plain language. What the number means for how you feel, eat and sleep.',
    tone: 'var(--ston-success)',
  },
  {
    key: 'Performance',
    label: 'Performance',
    copy: 'The same markers read against training load, recovery and output.',
    tone: 'var(--ston-purple-light)',
  },
  {
    key: 'Doctor Review',
    label: 'Clinical',
    copy: 'The clinical view — ready to take to the professional who guides you.',
    tone: '#60a5fa',
  },
];

const markers = [
  { name: 'Vitamin B12', value: '395 pg/mL', state: 'optimal' },
  { name: 'Vitamin D', value: '31.39 ng/ml', state: 'ok' },
  { name: 'Testosterone', value: '4.57 ng/dL', state: 'ok' },
];

const stateColor = {
  optimal: 'var(--ston-success)',
  ok: 'var(--ston-purple-light)',
  flag: '#f87171',
};

const AdvancedLabs = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-120px' });

  return (
    <section
      id="labs"
      ref={sectionRef}
      className="relative py-24 md:py-32 bg-ston-bg-alt overflow-hidden"
      data-testid="advanced-labs-section"
    >
      {/* Clinical blue-green ambience — deliberately cooler than the rest of the page */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 -left-40 w-[560px] h-[560px] rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(6,182,212,0.05), rgba(6,182,212,0.025) 55%, transparent 78%)' }} />
        <div className="absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(147,51,234,0.07), rgba(147,51,234,0.035) 55%, transparent 78%)' }} />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">

          {/* Left — the report */}
          <motion.div
            className="flex justify-center lg:justify-start order-2 lg:order-1"
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="relative">
              {/* Ghost score numeral, echoing the 78 on screen */}
              <GhostNumeral
                className="absolute -top-16 -left-12 md:-left-20"
                stroke="rgba(251,191,36,0.16)"
                size="clamp(140px, 15vw, 230px)"
              >
                78
              </GhostNumeral>

              <LiveScreen
                dir={1}
                src="/screens/labs-interpretation.webp"
                alt="A STON.FIT lab report scored 78, with everyday, performance and clinical interpretations"
                width={300}
                glowColor="rgba(96,165,250,0.22)"
              />

              {/* Marker readout floating alongside */}
              <motion.div
                className="hidden lg:block absolute -right-24 xl:-right-32 bottom-16 w-[228px] rotate-2 rounded-2xl border border-white/[0.1] bg-ston-card/90 backdrop-blur-xl p-4"
                style={{ boxShadow: '0 24px 60px -20px rgba(0,0,0,0.8)' }}
                initial={{ opacity: 0, y: 16 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.5 }}
              >
                <p className="section-label !text-[10px] !tracking-[2px] mb-3">Markers read</p>
                <ul className="space-y-2.5">
                  {markers.map((m, i) => (
                    <motion.li
                      key={m.name}
                      className="flex items-center justify-between gap-3"
                      initial={{ opacity: 0 }}
                      animate={isInView ? { opacity: 1 } : {}}
                      transition={{ duration: 0.4, delay: 0.7 + i * 0.18 }}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <span
                          className="w-1.5 h-1.5 rounded-full shrink-0"
                          style={{ background: stateColor[m.state] }}
                        />
                        <span className="text-[13px] text-ston-text-secondary truncate">
                          {m.name}
                        </span>
                      </span>
                      <span className="text-[13px] font-medium text-ston-text tabular-nums shrink-0">
                        {m.value}
                      </span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </motion.div>

          {/* Right — the argument */}
          <div className="order-1 lg:order-2">
            <motion.span
              className="section-label inline-block"
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              01 · READ
            </motion.span>

            <motion.h2
              className="mt-4 font-outfit font-bold text-ston-text leading-[1.08]"
              style={{ fontSize: 'clamp(32px, 4.4vw, 52px)', letterSpacing: '-1.8px' }}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.12 }}
            >
              One report.{' '}
              <span className="gradient-text">Three ways to read it.</span>
            </motion.h2>

            <motion.p
              className="mt-6 text-[17px] text-ston-text-secondary leading-relaxed max-w-[52ch]"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.22 }}
            >
              A lab report is forty markers and a column of reference ranges nobody
              explains. STON.FIT reads all of it — scores each marker, writes it back
              in language you actually use, and connects it to how you eat, sleep and
              train.
            </motion.p>

            {/* The three interpretation modes */}
            <div className="mt-10 space-y-3">
              {modes.map((mode, i) => (
                <motion.div
                  key={mode.key}
                  className="group relative flex gap-4 rounded-2xl border border-ston-border bg-ston-card/50 p-4 md:p-5 transition-colors duration-300 hover:border-white/[0.14] hover:bg-ston-card/80"
                  initial={{ opacity: 0, y: 16 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.4 + i * 0.12 }}
                >
                  <span
                    aria-hidden="true"
                    className="mt-1.5 h-8 w-[3px] rounded-full shrink-0"
                    style={{ background: mode.tone }}
                  />
                  <div>
                    <div className="flex items-baseline gap-2.5 flex-wrap">
                      <span className="text-[15px] font-semibold text-ston-text">
                        {mode.key}
                      </span>
                      <span
                        className="text-[10px] font-medium uppercase tracking-[1.5px]"
                        style={{ color: mode.tone }}
                      >
                        {mode.label}
                      </span>
                    </div>
                    <p className="mt-1 text-[14.5px] text-ston-text-secondary leading-relaxed">
                      {mode.copy}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.p
              className="mt-8 text-[13px] text-ston-text-muted max-w-[52ch] leading-relaxed"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.9 }}
            >
              Wellness insights, not medical advice. Discuss your results with your doctor.
            </motion.p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdvancedLabs;
